import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import {
  type SocialExportPublishForm,
  useSocialExportUploadConnectionFlow,
} from '@app/modules/social-export/hooks/useSocialExportUploadConnectionFlow';
import { publishMessages } from '@app/modules/social-export/publish';
import { getEditRenderedVideoUrl } from '@app/modules/video-editions';
import { webApi } from '@mi-front/api-clients';
import {
  SocialExportGetStationConnectionStateDto,
  useWebAppCreateSocialUploadCompositionsDetail,
  webAppGetCompositionsDetailSocialUploadsQueryKey,
  WebAppCreateSocialUploadDtoSourceTypeEnum,
} from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export type ComposeSocialExportPublishForm = SocialExportPublishForm;

/** Loaded composition and edits list (fetched by the route page before this hook runs). */
export interface UseComposeSocialExportUploadPageArgs {
  composition: webApi.EnrichedCompositionDetailDto;
  edits: webApi.EnrichedVideoEditorEditListItemDto[];
}

export interface UseComposeSocialExportUploadPageResult {
  platform: SocialPlatform;
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: ComposeSocialExportPublishForm;
  handleUpload: (publishData: Record<string, unknown>) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  setSelectedConnectionId: (id: string | null) => void;
  areConnectionsResolved: boolean;
  settingsQuery: ReturnType<typeof useSocialExportUploadConnectionFlow>['settingsQuery'];
  stationId: string;
}

/**
 * Routing, edit selection, preview URL, and create-upload mutation for composition social export.
 */
export function useComposeSocialExportUploadPage({
  composition,
  edits,
}: UseComposeSocialExportUploadPageArgs): UseComposeSocialExportUploadPageResult {
  const { platform: platformParam } = useParams<{
    compositionId: string;
    platform: string;
  }>();
  const platform = socialPlatforms.get(platformParam!.toUpperCase())!;
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const editIdFromUrl = searchParams.get('editId') ?? searchParams.get('projectId') ?? undefined;
  const editsWithRender = edits.filter((edit) => edit.renderJob?.status === 'COMPLETED');

  const [selectedSource, setSelectedSource] = useState<string>(() => editIdFromUrl ?? '');

  useEffect(() => {
    if (editIdFromUrl) {
      setSelectedSource(editIdFromUrl);
    }
  }, [editIdFromUrl]);

  useEffect(() => {
    if (editIdFromUrl || selectedSource) return;
    const firstId = editsWithRender[0]?.id;
    if (firstId) setSelectedSource(firstId);
  }, [editIdFromUrl, editsWithRender, selectedSource]);

  const selectedEdit =
    selectedSource !== '' ? (edits.find((edit) => edit.id === selectedSource) ?? null) : null;

  const { url: renderedVideoUrl } = getEditRenderedVideoUrl(selectedEdit, {
    mode: 'playback',
  });

  const showMediaSelector = editsWithRender.length > 0;
  const hasAvailableSources = editsWithRender.length > 0;
  const hasSelectedRenderedEdit = editsWithRender.some((edit) => edit.id === selectedSource);

  const stationId = composition.stationId ?? '';

  const {
    connections,
    selectedConnectionId,
    setSelectedConnectionId,
    areConnectionsResolved,
    isConnectionsLoading,
    settingsQuery,
    PublishForm,
  } = useSocialExportUploadConnectionFlow(stationId || undefined, platform);

  const uploadMutation = useWebAppCreateSocialUploadCompositionsDetail({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: webAppGetCompositionsDetailSocialUploadsQueryKey(composition.id),
        });
        notifyInfo($t(publishMessages.publicationStarted));
      },
      onError: () => {
        notifyError($t(publishMessages.startPublicationError));
      },
    },
  });

  const handleUpload = async (publishData: Record<string, unknown>) => {
    try {
      if (!hasSelectedRenderedEdit) {
        notifyError($t(publishMessages.noRenderedEdit));
        return;
      }

      if (!stationId) {
        notifyError($t(publishMessages.noStation));
        return;
      }

      if (isConnectionsLoading) {
        notifyError($t(publishMessages.connectionNotReady));
        return;
      }

      if (!selectedConnectionId) {
        notifyError(
          $t(publishMessages.noOAuthConnectionForPlatform, {
            platformName: platform.name,
          }),
        );
        return;
      }

      await uploadMutation.mutateAsync({
        id: composition.id,
        data: {
          sourceType: WebAppCreateSocialUploadDtoSourceTypeEnum.VIDEO_EDITOR_EDIT,
          sourceId: selectedSource,
          connectionId: selectedConnectionId,
          data: publishData,
        },
      });
      pushTo(composeRouteGenerator.publish(composition.id));
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  return {
    platform,
    selectedSource,
    setSelectedSource,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm,
    handleUpload,
    connections,
    selectedConnectionId,
    setSelectedConnectionId,
    areConnectionsResolved,
    settingsQuery,
    stationId,
  };
}
